import React from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { getDisplayName, buildDisplayNameMap } from '../utils/portfolioNames';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const hexToRgba = (hex, alpha) => {
    const h = (hex || '').replace('#', '');
    if (h.length !== 6) return `rgba(0, 51, 102, ${alpha})`;
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const ReturnsHistogramChart = React.forwardRef(({ histograms, portfolioRegistry }, ref) => {
    if (!histograms || histograms.length === 0) return null;

    const nameMap = buildDisplayNameMap(portfolioRegistry);

    // Fallback palette for callers that don't supply a colour per portfolio
    const fallbackColors = [
        { border: '#599BE3', background: 'rgba(89, 155, 227, 0.2)' },
        { border: '#91CF9B', background: 'rgba(145, 207, 155, 0.2)' },
        { border: '#f0a500', background: 'rgba(240, 165, 0, 0.2)' },
        { border: '#AA8090', background: 'rgba(170, 128, 144, 0.2)' },
        { border: '#39AEC5', background: 'rgba(57, 174, 197, 0.2)' },
        { border: '#305496', background: 'rgba(48, 84, 150, 0.2)' },
    ];
    const blendFallback = { border: '#072040', background: 'rgba(0, 51, 102, 0.3)' };

    // Build datasets
    const datasets = histograms.map((histogram, index) => {
        const color = histogram.colour
            ? { border: histogram.colour, background: hexToRgba(histogram.colour, 0.2) }
            : (histogram.is_blend ? blendFallback : fallbackColors[index % fallbackColors.length]);

        return {
            label: nameMap[histogram.name] || getDisplayName(histogram.name),
            data: histogram.frequencies,
            borderColor: color.border,
            backgroundColor: color.background,
            borderWidth: histogram.is_blend ? 3 : 2,
            fill: true,
            tension: 0.4,
            pointRadius: 0,
            pointHoverRadius: 4,
        };
    });

    // Use the bins from the first histogram (they're all the same)
    const labels = histograms[0].bins.map(b => (b * 100).toFixed(1) + '%');

    const chartData = {
        labels,
        datasets,
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        plugins: {
            legend: {
                display: true,
                position: 'top',
                labels: {
                    padding: 10,
                    usePointStyle: true,
                    font: {
                        size: 11
                    }
                }
            },
            tooltip: {
                callbacks: {
                    title: function(context) {
                        return 'Return: ' + context[0].label;
                    },
                    label: function(context) {
                        return context.dataset.label + ': ' + context.parsed.y.toFixed(4);
                    },
                },
            },
        },
        scales: {
            x: {
                display: true,
                title: {
                    display: true,
                    text: 'Annualised Return',
                    font: {
                        size: 12,
                        weight: 'bold'
                    }
                },
                ticks: {
                    maxTicksLimit: 10,
                },
                grid: {
                    display: false
                }
            },
            y: {
                display: true,
                title: {
                    display: true,
                    text: 'Probability Density',
                    font: {
                        size: 12,
                        weight: 'bold'
                    }
                },
                grid: {
                    color: 'rgba(0, 0, 0, 0.05)'
                }
            },
        },
    };

    return <Line ref={ref} data={chartData} options={options} />;
});

export default ReturnsHistogramChart;
